import { getEmulatorConfig } from './firebase';

export interface DiagnosticsConfig {
  enabled: boolean;
  authEmulatorEndpoint: string;
  showDetailedLogs: boolean;
  emulatorHost: string;
  emulatorPort: number;
  projectId: string;
}

// Safe environment variable access for browser
const getEnvVar = (name: string, defaultValue: string = ''): string => {
  try {
    // Vite environment variables (import.meta.env is available in Vite)
    if (import.meta.env) {
      return import.meta.env[name] || defaultValue;
    }
    // Fallback for other build systems or environments
    return defaultValue;
  } catch (error) {
    // If import.meta is not available, return default
    return defaultValue;
  }
};

// Check if we're in development mode
const isDevelopment = (): boolean => {
  try {
    // Vite development mode detection
    if (import.meta.env) {
      return import.meta.env.DEV || import.meta.env.MODE === 'development';
    }
    return false;
  } catch (error) {
    return false;
  }
};

// Get dynamic emulator configuration
const getEmulatorSettings = () => {
  try {
    const config = getEmulatorConfig();
    return {
      endpoint: config.url,
      host: config.host,
      port: config.port,
      projectId: config.projectId,
      isUsingEmulator: config.isUsingEmulator
    };
  } catch (error) {
    console.warn('Failed to get emulator settings:', error);
    return {
      endpoint: 'http://localhost:9099',
      host: 'localhost',
      port: 9099,
      projectId: 'demo-project',
      isUsingEmulator: false
    };
  }
};

const emulatorSettings = getEmulatorSettings();

export const diagnosticsConfig: DiagnosticsConfig = {
  enabled: isDevelopment() || 
           getEnvVar('VITE_ENABLE_DIAGNOSTICS') === 'true' ||
           getEnvVar('REACT_APP_ENABLE_DIAGNOSTICS') === 'true',
  authEmulatorEndpoint: emulatorSettings.endpoint,
  emulatorHost: emulatorSettings.host,
  emulatorPort: emulatorSettings.port,
  projectId: emulatorSettings.projectId,
  showDetailedLogs: true
};

// Function to toggle diagnostics at runtime
export const toggleDiagnostics = (enabled: boolean): void => {
  (diagnosticsConfig as any).enabled = enabled;
};

// Function to update emulator endpoint at runtime
export const updateEmulatorEndpoint = (host: string, port: number): void => {
  const newEndpoint = `http://${host}:${port}`;
  (diagnosticsConfig as any).authEmulatorEndpoint = newEndpoint;
  (diagnosticsConfig as any).emulatorHost = host;
  (diagnosticsConfig as any).emulatorPort = port;
  console.log(`🔧 Updated emulator endpoint to: ${newEndpoint}`);
};

// Helper function to get current emulator status
export const getEmulatorStatus = () => ({
  ...diagnosticsConfig,
  isUsingEmulator: emulatorSettings.isUsingEmulator,
  availableEnvVars: {
    // Vite environment variables
    'VITE_FIREBASE_AUTH_EMULATOR_HOST': getEnvVar('VITE_FIREBASE_AUTH_EMULATOR_HOST'),
    'VITE_FIREBASE_AUTH_EMULATOR_PORT': getEnvVar('VITE_FIREBASE_AUTH_EMULATOR_PORT'),
    'VITE_FIREBASE_AUTH_EMULATOR_URL': getEnvVar('VITE_FIREBASE_AUTH_EMULATOR_URL'),
    'VITE_USE_FIREBASE_EMULATOR': getEnvVar('VITE_USE_FIREBASE_EMULATOR'),
    'VITE_ENABLE_DIAGNOSTICS': getEnvVar('VITE_ENABLE_DIAGNOSTICS'),
    // Create React App environment variables
    'REACT_APP_FIREBASE_AUTH_EMULATOR_HOST': getEnvVar('REACT_APP_FIREBASE_AUTH_EMULATOR_HOST'),
    'REACT_APP_FIREBASE_AUTH_EMULATOR_PORT': getEnvVar('REACT_APP_FIREBASE_AUTH_EMULATOR_PORT'),
    'REACT_APP_FIREBASE_AUTH_EMULATOR_URL': getEnvVar('REACT_APP_FIREBASE_AUTH_EMULATOR_URL'),
    'REACT_APP_USE_FIREBASE_EMULATOR': getEnvVar('REACT_APP_USE_FIREBASE_EMULATOR'),
    'REACT_APP_ENABLE_DIAGNOSTICS': getEnvVar('REACT_APP_ENABLE_DIAGNOSTICS'),
    // General environment info
    'MODE': getEnvVar('MODE'),
    'DEV': getEnvVar('DEV')
  }
});
